import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AlertCircle } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";

export default function LoginPage() {
    const { login } = useAuth();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        try {
            await login(email, password);
            navigate("/");
        } catch (err) {
            setError(formatApiError(err.response?.data?.detail) || err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="max-w-md mx-auto px-4 sm:px-6 py-16" data-testid="login-page">
            <div className="panel p-8 sm:p-9">
                <span className="eyebrow inline-block border-2 border-foreground bg-accent px-2 py-1 text-accent-foreground">Members</span>
                <h1 className="font-display uppercase text-2xl sm:text-3xl tracking-[-0.015em] mt-2.5">Welcome back</h1>
                <p className="mt-2.5 text-sm text-dim leading-relaxed">
                    Log in to manage your subscription preferences.
                </p>

                <form onSubmit={submit} className="mt-8 space-y-5">
                    <div>
                        <label className="eyebrow text-dim block mb-2">Email</label>
                        <input
                            type="email"
                            required
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            data-testid="login-email"
                            className="field"
                        />
                    </div>
                    <div>
                        <label className="eyebrow text-dim block mb-2">Password</label>
                        <input
                            type="password"
                            required
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            data-testid="login-password"
                            className="field"
                        />
                        <Link
                            to="/forgot-password"
                            data-testid="login-forgot"
                            className="mt-2.5 inline-block font-mono text-[11px] uppercase tracking-wider bg-accent text-accent-foreground px-1 link-underline"
                        >
                            Forgot password?
                        </Link>
                    </div>
                    {error && (
                        <p
                            className="flex items-start gap-2.5 border-[3px] border-foreground bg-neg/25 p-3 text-sm font-medium text-foreground"
                            data-testid="login-error"
                        >
                            <AlertCircle size={16} className="shrink-0 mt-0.5" />
                            {error}
                        </p>
                    )}
                    <button
                        type="submit"
                        disabled={loading}
                        data-testid="login-submit"
                        className="w-full border-[3px] border-foreground bg-accent text-accent-foreground py-3 text-sm font-bold uppercase tracking-wider shadow-card hover:translate-x-0.5 hover:translate-y-0.5 hover:shadow-inset transition-[transform,box-shadow] duration-150 disabled:opacity-50 disabled:hover:translate-x-0 disabled:hover:translate-y-0 disabled:hover:shadow-card"
                    >
                        {loading ? "Logging in…" : "Log in"}
                    </button>
                </form>

                <p className="mt-7 pt-6 border-t-[3px] border-foreground text-sm text-dim">
                    No account? <Link to="/register" className="bg-accent text-accent-foreground px-1 link-underline font-medium">Subscribe free</Link>
                </p>
            </div>
        </div>
    );
}
